import React, { useState, useEffect, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { Oval as Loader } from "react-loader-spinner";

import "../home.css";
import { ROLE } from "../../../../../shared/constants/common";
import FeedPost from "../../../../../features/cards/user/feed";
import NoContent from "../../../../component/NoContent/NoContent";
import {
  fetchFeed,
  fetchCreatorContentById,
  updateFeedItem
} from "../../../../../shared/actions/contentAction";
import RecommendedCreators from "../../../../component/Recommended/Creators";
import { resolveVatRate } from "../../../../../shared/utils/paymentUtil";
import { reportError } from "../../../../../shared/sentry";

const FeedTab = ({
  userInfo,
  fetchFeed,
  feed,
  feedHasMore,
  feedPage,
  updateFeedItem,
}) => {
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [limit] = useState(6);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [processedFeedData, setProcessedFeedData] = useState([]);
  const loadMoreSentinelRef = useRef(null);
  const [vatPercentage, setVatPercentage] = useState(0);
  
  // Effect to fetch VAT percentage
  useEffect(() => {
    const fetchVatPercentage = async () => {
      const vatPercentage = await resolveVatRate(userInfo);
      setVatPercentage(vatPercentage);
    };
    fetchVatPercentage();
  }, [userInfo]);

  const processFeedData = useCallback((data) => {
    const feedData = data || [];

    if (!userInfo) {
      return feedData;
    }

    return feedData.map(item => {
      // If the creator role is CREATOR_SUB, set subscribed to true in profile
      return {
        ...item,
        profile: {
          ...item.profile,
          subscribed: true
        }
      }

      return item;
    });
  }, [userInfo]);

  const getFeed = useCallback(async (page = 0, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await fetchFeed(limit, page, isLoadMore);

      if (response.success) {
        setHasInitiallyLoaded(true);
      }
    } catch (error) {
      reportError(error, 'FeedTab fetch feed');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [limit, fetchFeed]);

  useEffect(() => {
    if (feed?.length > 0 && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true);
      setLoading(false);
    } else if (!hasInitiallyLoaded) {
      getFeed(0, false);
    }
  }, [feed, hasInitiallyLoaded, getFeed]);

  useEffect(() => {
    setProcessedFeedData(processFeedData(feed || []));
  }, [feed, processFeedData]);

  const loadMore = useCallback(() => {
    // Check if there's more data available and not currently loading
    if (!feedHasMore || loadingMore || loading) {
      return;
    }

    getFeed(feedPage + 1, true);
  }, [feedHasMore, loadingMore, loading, feedPage, getFeed]);

  // Setup Intersection Observer for load more (more reliable across browsers)
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) return;

    // Use Intersection Observer API for better cross-browser compatibility
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        // When sentinel element is visible (or near visible), load more
        if (entry.isIntersecting) {
          loadMore();
        }
      },
      {
        root: null, // viewport
        rootMargin: '200px', // Trigger 200px before reaching the sentinel
        threshold: 0.1 // Trigger when 10% of sentinel is visible
      }
    );

    observer.observe(sentinel);

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [loadMore]);

  const handleDropdownToggle = useCallback((postId) => {
    setOpenDropdownId(prev => prev === postId ? null : postId);
  }, []);

  // after refresh purchased content
  const onPurchaseSuccess = useCallback(async (purchasedPost) => {
    const creatorId = purchasedPost?.profile?.userid;
    const contentId = purchasedPost?._id;

    if (!creatorId || !contentId) {
      return;
    }

    try {
      const response = await fetchCreatorContentById(creatorId, contentId);

      if (!response.success || !response.data) {
        return;
      }

      const extractPostFromResponse = (data) => {
        if (!data) {
          return null;
        }

        if (Array.isArray(data)) {
          return data[0] || null;
        }

        if (data.content && typeof data.content === "object") {
          return data.content;
        }

        if (data.data && typeof data.data === "object") {
          return data.data;
        }

        if (data.post) {
          return data.post;
        }

        return data;
      };

      const updatedPost = extractPostFromResponse(response.data);
      if (!updatedPost) {
        return;
      }

      const getComparableId = (post) =>
        post?._id || post?.content_id || post?.content?._id;

      const updatedPostId = getComparableId(updatedPost);

      if (!updatedPostId) {
        return;
      }

      updateFeedItem(updatedPost);

      const mergePosts = (existingPost) => {
        const mergedPost = { ...existingPost, ...updatedPost };

        if (existingPost?.content || updatedPost?.content) {
          mergedPost.content = {
            ...(existingPost?.content || {}),
            ...(updatedPost?.content || {}),
          };
        }

        if (existingPost?.profile || updatedPost?.profile) {
          mergedPost.profile = {
            ...(existingPost?.profile || {}),
            ...(updatedPost?.profile || {}),
          };
        }

        return mergedPost;
      };

      setProcessedFeedData((prev) => {
        if (!Array.isArray(prev) || prev.length === 0) {
          return prev;
        }

        let hasChanges = false;

        const nextList = prev.map((item) => {
          const itemId = getComparableId(item);
          if (itemId === updatedPostId) {
            hasChanges = true;
            return mergePosts(item);
          }
          return item;
        });

        if (!hasChanges) {
          return prev;
        }

        return processFeedData(nextList);
      });
    } catch (error) {
      reportError(error, 'FeedTab refresh purchased content');
    }
  }, [processFeedData, fetchCreatorContentById, updateFeedItem]);

  if (loading && !hasInitiallyLoaded) {
    return (
      <div className="modern-feed-loading">
        <div>
          <Loader color="#F334BE" height={30} width={30} />
          <h5>Loading feed...</h5>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-feed">
      <div className="feed-container">
        {processedFeedData.length > 0 && processedFeedData.map((item, index) => (
          <React.Fragment key={item._id}>
            <div data-content-id={item._id}>
              <FeedPost
                postId={item._id || index}
                post={item}
                creatorRole={item?.profile?.rol || null}
                isCreatorOwnProfile={userInfo?._id === item?.profile?._id}
                creatorInfo={item?.profile || {}}
                openDropdownId={openDropdownId}
                onDropdownToggle={handleDropdownToggle}
                isShowDuration={true}
                showControls={true}
                isPPVContent={item?.profile?.rol === ROLE.CREATOR_CONTENT}
                isSubContent={item?.profile?.rol === ROLE.CREATOR_SUB}
                isShowChatButton={true}
                isShowEllipsis={false}
                onPurchaseSuccess={onPurchaseSuccess}
                onOpenFollowModal={() => { }}
                priority={index < 2}
              />
            </div>
            {/* Show Recommended Creators after every 6 posts */}
            {(index + 1) % 6 === 0 && (
              <RecommendedCreators
                vatPercentage={vatPercentage}
              />
            )}
          </React.Fragment>
        ))}

        {!loading && hasInitiallyLoaded && processedFeedData.length === 0 && (
          <NoContent
            isLoading={loading}
            hasData={processedFeedData.length > 0}
            message="Nothing here yet!"
          />
        )}

        {/* Sentinel element for Intersection Observer - triggers load more when visible */}
        {hasInitiallyLoaded && feedHasMore && !loadingMore && (
          <div ref={loadMoreSentinelRef} style={{ height: '1px', width: '100%' }} />
        )}

        {loadingMore && feedHasMore && (
          <div className="loading-more">
            <div className="loading-more-spinner">
              <Loader type="Oval" color="#F334BE" height={40} width={40} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userInfo: state.user.user,
  feed: state.content?.feed || [],
  feedHasMore: state.content?.feedHasMore ?? true,
  feedPage: state.content?.feedPage || 0,
});

const mapDispatchToProps = {
  fetchFeed,
  updateFeedItem,
};

export default connect(mapStateToProps, mapDispatchToProps)(FeedTab); 